import { computed } from '@vue/composition-api'
import { CastType } from '@/types/casts'
import { hasFeature, Feature } from '@/featureFlags/featureFlags'
import useTranslations from '@/compositions/translations'
import { TranslationKeys } from '@/plugins/translations/types'

interface CastTypeConfig {
  hasFeature: boolean|null,
  type: CastType
}

const useCastTypes = () => {
  const translations = useTranslations()

  const getCastTypeName = (castType: CastType|null): string => {
    if (castType === null) return ''
    const translationKeys: Record<CastType, TranslationKeys> = {
      [CastType.Plain]: 'casttypeselector_plain_name',
      [CastType.Switcher]: 'casttypeselector_switcher_name',
      [CastType.FanRoom]: 'casttypeselector_fanroom_name',
      [CastType.StationSwitcher]: 'casttypeselector_stationswitcher_name'
    }
    return translations.getTranslation(translationKeys[castType])?.toString() ?? ''
  }

  const castTypeOptions = computed(() => {
    const castTypeConfig: Record<CastType, CastTypeConfig> = {
      [CastType.Switcher]: {
        hasFeature: true,
        type: CastType.Switcher
      },
      [CastType.Plain]: {
        hasFeature: false,
        type: CastType.Plain
      },
      [CastType.FanRoom]: {
        hasFeature: hasFeature(Feature.FanRoom),
        type: CastType.FanRoom
      },
      [CastType.StationSwitcher]: {
        hasFeature: hasFeature(Feature.FanRoom),
        type: CastType.StationSwitcher
      }
    }

    const options: CastType[] = []
    for (const config of Object.values(castTypeConfig)) {
      if (!config.hasFeature) continue
      options.push(config.type)
    }

    return options
  })

  const castTypeSelectOptions = computed(() => {
    const options = castTypeOptions.value.map((castType) => ({ label: getCastTypeName(castType), value: castType }))
    return [{ label: 'All event types', value: null }, ...options]
  })

  return {
    castTypeOptions,
    castTypeSelectOptions,
    getCastTypeImgUrl: (castType: CastType) => require(`@/assets/casttypes/illustrations/${ castType }.svg`),
    getCastTypeIconUrl: (castType: CastType) => require(`@/assets/icons/${ castType }.svg`),
    getCastTypeName,
    getCastTypeShortDescription (castType: CastType|null): string {
      if (castType === null) return ''
      const translationKeys: Record<CastType, TranslationKeys> = {
        [CastType.Plain]: 'casttypeselector_plain_short_description',
        [CastType.Switcher]: 'casttypeselector_switcher_short_description',
        [CastType.FanRoom]: 'casttypeselector_fanroom_short_description',
        [CastType.StationSwitcher]: 'casttypeselector_stationswitcher_short_description'
      }
      return translations.getTranslation(translationKeys[castType])?.toString() ?? ''
    },
    getCastTypeDescription (castType: CastType|null): string {
      if (castType === null) return ''
      const translationKeys: Record<CastType, TranslationKeys> = {
        [CastType.Plain]: 'casttypeselector_plain_description',
        [CastType.Switcher]: 'casttypeselector_switcher_description',
        [CastType.FanRoom]: 'casttypeselector_fanroom_description',
        [CastType.StationSwitcher]: 'casttypeselector_stationswitcher_description'
      }
      return translations.getTranslation(translationKeys[castType])?.toString() ?? ''
    }
  }
}

export default useCastTypes
